// CreatorAvatarModelsPanel.tsx — modèles 3D (effet Avatar) d'un créateur, dans l'onglet Ressources de sa fiche
//
// Chaque tuile montre une image du modèle (fabriquée dans le navigateur, cf. avatarModelView) ;
// un clic ouvre le modèle en grand, en rotation, manipulable à la souris. Lecture seule : on
// ajoute et on retire les modèles depuis l'effet Avatar de la régie.
// Données : admin-api AvatarModelController (/api/admins/{id}/avatar_models).

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import styles from './ChannelsSection.module.scss';
import { size } from './dashboardShared';
import { API_URLS } from '../../../backendServices/urls';
import { renderAvatarModelThumb, openAvatarModelViewer, AvatarModelViewer } from './avatarModelView';

interface Model {
  id: string; label: string; family: 'face' | 'hand' | 'decoration'; anchor: string;
  side?: 'left' | 'right' | null; size: number; originalName?: string; createdAt?: string;
}

const FAMILY: Record<Model['family'], string> = { face: 'Visage', hand: 'Main', decoration: 'Décoration' };
const familyLabel = (m: Model) => m.family === 'hand' && m.side ? `Main ${m.side === 'left' ? 'gauche' : 'droite'}` : FAMILY[m.family];

const fileUrl = (adminId: number, m: Model) =>
  API_URLS.getAdminAvatarModelFile.replace('{id}', `${adminId}`).replace('{modelId}', m.id);

const Thumb: React.FC<{ url: string }> = ({ url }) => {
  const [ src, setSrc ] = useState<string | null>(null);
  const [ failed, setFailed ] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setSrc(null); setFailed(false);
    renderAvatarModelThumb(url).then(d => { if (!cancelled) setSrc(d); }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [url]);
  return (
    <div className={styles.thumb} style={{ background: 'linear-gradient(160deg, #1e293b, #0f172a)' }}>
      {src    && <img src={src} alt="" style={{ height: '100%', objectFit: 'contain' }} />}
      {!src   && <span className={styles.thumbNameBig}>{failed ? 'Illisible' : '…'}</span>}
    </div>
  );
};

const Viewer: React.FC<{ url: string; title: string; onClose: () => void }> = ({ url, title, onClose }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const viewer = useRef<AvatarModelViewer | null>(null);
  const [ state, setState ] = useState<{ status: 'loading' | 'ready' | 'error'; animations: number }>({ status: 'loading', animations: 0 });
  const [ spinning, setSpinning ] = useState(true);

  useEffect(() => {
    viewer.current = openAvatarModelViewer(canvas.current!, url, setState);
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('keydown', key); viewer.current?.dispose(); viewer.current = null; };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(2,6,23,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div style={{ width: 'min(900px, 94vw)', height: 'min(700px, 88vh)', background: 'radial-gradient(circle at 50% 35%, #1e293b, #020617)', borderRadius: 14,
                    border: '1px solid #334155', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#e2e8f0' }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
          <strong style={{ flex: 1, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</strong>
          <button type="button" className={styles.secondary} style={{ flex: 'none' }}
                  onClick={() => { const next = !spinning; viewer.current?.setSpinning(next); setSpinning(next); }}>
            {spinning ? 'Pause' : 'Rotation'}
          </button>
          <button type="button" className={styles.secondary} style={{ flex: 'none' }} onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <canvas ref={canvas} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: 'grab' }} />
          {state.status === 'loading' && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Chargement du modèle…</div>}
          {state.status === 'error'   && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fca5a5' }}>Ce fichier n'a pas pu être lu.</div>}
        </div>
        <div style={{ padding: '8px 16px 12px', fontSize: 12, color: '#94a3b8' }}>
          Glisser pour tourner · molette pour zoomer{state.animations > 0 ? ` · ${state.animations} animation${state.animations > 1 ? 's' : ''} jouée${state.animations > 1 ? 's' : ''}` : ''}
        </div>
      </div>
    </div>
  );
};

const CreatorAvatarModelsPanel: React.FC<{ adminId: number }> = ({ adminId }) => {
  const [ models  , setModels  ] = useState<Model[]>([]);
  const [ loading , setLoading ] = useState(true);
  const [ error   , setError   ] = useState(false);
  const [ opened  , setOpened  ] = useState<Model | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    axios.get(API_URLS.getAdminAvatarModels.replace('{id}', `${adminId}`))
      .then(r => { if (!cancelled) setModels(r.data as Model[]); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminId]);

  if (loading) return <span>Chargement des modèles 3D…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (models.length === 0) return <div className={styles.empty}>Aucun modèle 3D. Ils s'ajoutent depuis l'effet Avatar de la régie.</div>;

  return (
    <div>
      <div className={styles.cards}>
        {[ ...models ].reverse().map(m => (
          <article key={m.id} className={styles.card} style={{ cursor: 'pointer' }} onClick={() => setOpened(m)}>
            <Thumb url={fileUrl(adminId, m)} />
            <div className={styles.cardBody}>
              <span className={styles.cardName} title={m.label}>{m.label || '—'}</span>
              <div className={styles.chips}>
                <span>{familyLabel(m)}</span>
                {size(m.size) && <span>{size(m.size)}</span>}
                {m.createdAt && <span>Ajouté le {new Date(m.createdAt).toLocaleDateString('fr-FR')}</span>}
              </div>
              <div className={styles.actions}>
                <button type="button" className={styles.primary} onClick={e => { e.stopPropagation(); setOpened(m); }}>Voir en 3D</button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {opened && <Viewer url={fileUrl(adminId, opened)} title={opened.label} onClose={() => setOpened(null)} />}
    </div>
  );
};

export default CreatorAvatarModelsPanel;
